import { Button, Card, Icon } from "../../../components";
import { useEffect, useState, useCallback, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Dashboard from "../../../layout/Dashboard";
import { formatBRL } from "../../../utils/Formats";
import React from "react";
import { CardSelect, CupomModal } from "../Forms";
import { CouponAppliedSection } from "../CouponUi";
import { Api } from "../../../api/Assinafy";
import { normalizeCouponCode } from "../../../utils/couponBenefits";
import PaymentProcessing from "../PaymentProcessing";
import { SessionContext } from "../../../routes/DashboardRoute"

import "./styles.scss";

const ExtraCredits = () => {
  const navigate = useNavigate();
  const isMountedRef = useRef(true);
  const timeoutRef = useRef(null);
  const [session] = useContext(SessionContext)
  
  const { user } = session || { user: {} }
  const [modal, setModal] = useState(false);
  const [creditAmount, setCreditAmount] = useState(10);

  const [loadingCards, setLoadingCards] = useState(true);
  const [creditCards, setCreditCards] = useState([]);
  const [paymentData, setPaymentData] = useState({
    isNewCard: true,
    selectedCardId: null,
    newCardData: {},
    isNewCardValid: false,
  });
  const [forceValidation, setForceValidation] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const [currentPlan, setCurrentPlan] = useState(null);
  const [needsCustomerUpdate, setNeedsCustomerUpdate] = useState(false);

  const [appliedCoupon, setAppliedCoupon] = useState(null);

  const userName = user?.name || "Usuário"

  const changeCreditAmount = (i) => {
    const newAmount = (Number(creditAmount) || 10) + i;
    if (newAmount >= 10) {
      setCreditAmount(newAmount);
    }
  };

  const handleCreditAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setCreditAmount(value === '' ? '' : parseInt(value, 10));
    }
  };

  const handleCreditAmountBlur = () => {
    if ((Number(creditAmount) || 0) < 10) setCreditAmount(10);
  };

  const price = 1;
  const total = Math.max(0, (Number(creditAmount) || 0) * price);

  const fetchSubscriptionData = async () => {
    try {
      const response = await Api("subscription/view")();
      
      if (!isMountedRef.current) return;

      if (response?.status === 200 && response?.data?.data) {
        setCurrentPlan(response.data.data);
      }
    } catch (error) {
      console.error("Erro ao buscar dados do plano:", error);
    }
  };

  const fetchCreditCards = async () => {
    try {
      setLoadingCards(true);
      const response = await Api("creditCard/index")();

      if (!isMountedRef.current) return;

      if (response?.status === 200 && response?.data?.data) {
        setCreditCards(response.data.data);
        if (response.data.data.length > 0) {
          setPaymentData({
            isNewCard: false,
            selectedCardId: response.data.data[0].id,
            newCardData: {},
            isNewCardValid: false,
          });
        }
      } else {
        setCreditCards([]);
      }
    } catch (error) {
      console.error("Erro ao buscar cartões:", error);
      if (isMountedRef.current) {
        setCreditCards([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingCards(false);
      }
    }
  };

  const fetchCustomerData = async () => {
    try {
      const response = await Api("customer/view")();

      if (!isMountedRef.current) return;

      if (response?.status === 200 && response?.data?.data) {
        const customer = response.data.data;
        const requiredFields = ["name", "email", "taxpayer_id", "postal_code", "address_line1", "city", "state"];
        const missingFields = requiredFields.filter((field) => !customer[field]);

        if (missingFields.length > 0) {
          setNeedsCustomerUpdate(true);
        }
      } else {
        setNeedsCustomerUpdate(true);
      }
    } catch (error) {
      console.error("Erro ao buscar dados do cliente:", error);
      if (isMountedRef.current) {
        setNeedsCustomerUpdate(true);
      }
    }
  };

  const updateCustomerData = async (cardData) => {
    try {
      const customerPayload = {
        name: cardData.holder_name,
        taxpayer_id: cardData.holder_taxpayer_id.replace(/\D/g, ""),
        phone_number: cardData.holder_phone.replace(/\D/g, ""),
        email: cardData.holder_email,
        address: {
          line1: cardData.address_line1,
          line2: cardData.address_line2 || "",
          house_number: cardData.house_number,
          neighborhood: cardData.neighborhood,
          city: cardData.city,
          state: cardData.state,
          postal_code: cardData.postal_code.replace(/\D/g, ""),
        },
      };

      const response = await Api("customer/update")(customerPayload);

      if (!isMountedRef.current) return false;

      if (response?.status === 200) {
        setNeedsCustomerUpdate(false);
        return true;
      } else {
        throw new Error("Erro ao atualizar dados do cliente");
      }
    } catch (error) {
      console.error("Erro ao atualizar dados do cliente:", error);
      throw error;
    }
  };

  const handlePurchaseExtraCredits = async () => {
    if (isProcessing) return;

    if (!paymentData.isNewCard && !paymentData.selectedCardId) {
      alert("Por favor, selecione um cartão ou adicione um novo.");
      return;
    }

    if (paymentData.isNewCard && !paymentData.isNewCardValid) {
      setForceValidation(true);
      return;
    }

    try {
      setIsProcessing(true);
      setForceValidation(false);
      setPaymentStatus("processing");

      if (paymentData.isNewCard && paymentData.newCardData) {
        const cardData = paymentData.newCardData;

        if (needsCustomerUpdate) {
          await updateCustomerData(cardData);
        }

        const creditCardPayload = {
          number: cardData.number.replace(/\s/g, ""),
          name_on_card: cardData.name_on_card,
          expiration_month: parseInt(cardData.expiration_month),
          expiration_year: parseInt(cardData.expiration_year),
          security_code: parseInt(cardData.security_code),
          holder: {
            name: cardData.holder_name,
            email: cardData.holder_email,
            taxpayer_id: cardData.holder_taxpayer_id.replace(/\D/g, ""),
            postal_code: cardData.postal_code.replace(/\D/g, ""),
            address_line1: cardData.address_line1,
            address_line2: cardData.address_line2 || "",
            house_number: cardData.house_number,
            phone_number: cardData.holder_phone.replace(/\D/g, ""),
            neighborhood: cardData.neighborhood,
            city: cardData.city,
            state: cardData.state,
          },
        };

        const cardResponse = await Api("creditCard/create")(creditCardPayload);

        if (cardResponse?.status !== 200) {
          throw new Error(cardResponse?.data?.message || "Erro ao criar cartão de crédito");
        }
      }

      const purchasePayload = {
        amount: Number(creditAmount)
      };

      if (!paymentData.isNewCard && paymentData.selectedCardId) {
        purchasePayload.credit_card_id = paymentData.selectedCardId;
      } else if (paymentData.isNewCard && paymentData.newCardData) {
        const cardData = paymentData.newCardData;
        purchasePayload.credit_card = {
          number: cardData.number.replace(/\s/g, ""),
          name_on_card: cardData.name_on_card,
          expiration_month: parseInt(cardData.expiration_month),
          expiration_year: parseInt(cardData.expiration_year),
          security_code: parseInt(cardData.security_code),
          holder: {
            name: cardData.holder_name,
            email: cardData.holder_email,
            taxpayer_id: cardData.holder_taxpayer_id.replace(/\D/g, ""),
            postal_code: cardData.postal_code.replace(/\D/g, ""),
            address_line1: cardData.address_line1,
            address_line2: cardData.address_line2 || "",
            house_number: cardData.house_number,
            phone_number: cardData.holder_phone.replace(/\D/g, ""),
            neighborhood: cardData.neighborhood,
            city: cardData.city,
            state: cardData.state,
          },
        };
      }

      const response = await Api("credit/purchase")(purchasePayload);

      if (!isMountedRef.current) return;

      if (response?.status === 200) {
        if (appliedCoupon?.code) {
          const redeemResponse = await Api("coupon/redeem")(appliedCoupon.code);

          if (!isMountedRef.current) return;

          if (redeemResponse?.status !== 200) {
            throw new Error(
              redeemResponse?.data?.message || "Compra realizada, mas não foi possível resgatar o cupom"
            );
          }
        }

        setPaymentStatus("success");
        timeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            navigate("/dashboard/workspace/plan");
          }
        }, 3000);
      } else {
        throw new Error("Erro ao comprar créditos");
      }
    } catch (error) {
      console.error("Erro no pagamento:", error);
      if (isMountedRef.current) {
        setPaymentStatus("failure");
      }
    } finally {
      if (isMountedRef.current) {
        setIsProcessing(false);
      }
    }
  };

  const handleFormChange = useCallback((data) => {
    setPaymentData({
      isNewCard: true,
      selectedCardId: null,
      newCardData: data.formData,
      isNewCardValid: data.isValid,
    });
  }, []);

  const handleValidateCoupon = async (couponCode) => {
    try {
      const normalizedCoupon = normalizeCouponCode(couponCode);
      const validateResponse = await Api("coupon/validate")(normalizedCoupon);

      if (validateResponse?.status !== 200) {
        return {
          success: false,
          message: validateResponse?.data?.message || "Cupom inválido",
        };
      }

      const couponData = validateResponse?.data?.data || {};

      if (couponData.is_redeemable_on_plan_change_only) {
        return {
          success: false,
          message: "Este cupom só pode ser usado na contratação de plano pago.",
        };
      }

      return {
        success: true,
        code: normalizedCoupon,
        coupon: couponData,
      };
    } catch (error) {
      return {
        success: false,
        message: error?.response?.data?.message || "Não foi possível validar o cupom",
      };
    }
  };

  const handleReturnFromProcessing = () => {
    if (paymentStatus === "success") {
      navigate("/dashboard/workspace");
    } else if (paymentStatus === "failure") {
      setPaymentStatus(null);
      setForceValidation(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionData();
    fetchCreditCards();
    fetchCustomerData();

    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (paymentStatus) {
    return <PaymentProcessing state={paymentStatus} data={{ total, name: userName }} onReturn={handleReturnFromProcessing} />;
  }

  return (
    <Dashboard.Content style={{ maxWidth: "688px" }}>
      <Dashboard.Header
        returnButton={true}
        title="Comprar créditos"
        className="d-flex reverse-row"
      />
      <div className="plan-change-status immediate extraCredits-balance">
        <Icon id="icon-check_circle" size="22" />
        <div>
          <strong>Seus créditos não expiram</strong>
          <span>
            {currentPlan?.balances?.credits?.balance != null
              ? `Saldo atual: ${formatBRL(currentPlan.balances.credits.balance)} em créditos.`
              : "Use quando precisar em recursos e serviços do Assinafy."}
          </span>
        </div>
      </div>
      <Card className="plan-change-card extraCredits-card">
        <div className="plan-change-hero extraCredits-hero">
          <div>
            <span className="plan-change-kicker">Créditos</span>
            <h2>Créditos</h2>
            <p><Icon id="icon-coin" size="18" /> Disponíveis logo após a confirmação</p>
          </div>
        </div>

        <div className="extraCredits-content">
          <div className="extraCredits-selector">
            <label htmlFor="credit-amount">Quantos créditos você quer comprar?</label>
            <div className="extraCredits-stepper">
              <Button aria-label="Remover um crédito" onClick={() => changeCreditAmount(-1)} disabled={(Number(creditAmount) || 0) <= 10}>
                <Icon id="icon-minus" />
              </Button>
              <input
                id="credit-amount"
                type="text"
                inputMode="numeric"
                aria-label="Quantidade de créditos"
                value={creditAmount}
                onChange={handleCreditAmountChange}
                onBlur={handleCreditAmountBlur}
                min="10"
              />
              <Button aria-label="Adicionar um crédito" onClick={() => changeCreditAmount(+1)}>
                <Icon id="icon-plus" />
              </Button>
            </div>
            <div className="extraCredits-presets" aria-label="Quantidades sugeridas">
              {[10, 25, 50, 100].map((amount) => (
                <Button
                  key={amount}
                  className={Number(creditAmount) === amount ? "active" : ""}
                  onClick={() => setCreditAmount(amount)}
                >
                  {amount}
                </Button>
              ))}
            </div>
            <small>Compra mínima de 10 créditos.</small>
          </div>

          <div className="plan-change-values extraCredits-values">
            <div><span>Valor unitário</span><strong>{formatBRL(price)}</strong></div>
            <div><span>Quantidade</span><strong>{Number(creditAmount) || 0} créditos</strong></div>
            <div className="total"><span>Você paga hoje</span><strong>{formatBRL(total)}</strong></div>
          </div>

          <div className="plan-change-coupon">
            <CouponAppliedSection
              coupon={appliedCoupon}
              code={appliedCoupon?.code}
              context="credits"
              purchasedCredits={Number(creditAmount) || 0}
              onRemove={() => setAppliedCoupon(null)}
              onChange={() => setModal(true)}
            />
          </div>
        </div>
      </Card>

      <section className="plan-change-payment">
        <h3>
          {loadingCards
            ? "Carregando..."
            : creditCards.length > 0
              ? "Escolha como pagar"
              : "Insira os dados do cartão"}
        </h3>
        <div className="card-select pb-3">
          {loadingCards ? (
            <div className="text-center p-4">
              <div className="spinner-border" role="status">
                <span className="sr-only">Carregando...</span>
              </div>
            </div>
          ) : (
            <CardSelect
              creditCards={creditCards}
              onCardSelect={setPaymentData}
              onFormChange={handleFormChange}
              forceValidation={forceValidation}
            />
          )}
        </div>
      </section>

      <div className="plan-change-actions">
        <p className="plan-change-safe"><Icon id="icon-lock" size="16" /> Pagamento processado com segurança</p>
        <Button variant="border" onClick={() => navigate("/dashboard/workspace/plan")} disabled={isProcessing}>
          Voltar
        </Button>
        <Button 
          variant="primary" 
          className="text-uppercase btn-wide btn-responsive"
          onClick={handlePurchaseExtraCredits}
          disabled={
            isProcessing ||
            (Number(creditAmount) || 0) < 10 ||
            (!paymentData.isNewCard && !paymentData.selectedCardId) ||
            (paymentData.isNewCard && !paymentData.isNewCardValid)
          }
        >
          {isProcessing ? "Processando..." : `Comprar ${Number(creditAmount) || 0} créditos`}
        </Button>
      </div>
      <CupomModal
        show={modal}
        onClose={() => setModal(false)}
        cupom={appliedCoupon?.code}
        context="credits"
        onValidateCoupon={handleValidateCoupon}
        onConfirmCoupon={setAppliedCoupon}
      />
    </Dashboard.Content>
  );
};

export default ExtraCredits;
